import { useContext, useMemo } from 'react'
import usePropertyBedbankViews from './usePropertyBedbankViews'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { min } from 'lib/array/arrayUtils'
import { getCheapestSelectedPackageOption, getCheapestAvailablePackageOption } from 'selectors/offerPage/offerPageSelectors'

type Return = [
  hotelPricing: App.PricingWithValue,
  memberPricing: App.PricingWithValue | undefined,
]

function useCheapestPropertyPricing(propertyId: string): Return {
  const offerPageState = useContext(OfferPageStateContext)
  const [bedbankPackageViews] = usePropertyBedbankViews(propertyId)
  const cheapestBedbankView = useMemo(() => {
    const allRates = bedbankPackageViews.flatMap(view => view.rates)
    return min(allRates, rate => rate.totals.memberInclusive || rate.totals.inclusive)
  }, [bedbankPackageViews])

  const cheapestPackageOption = useAppSelector(state => {
    return getCheapestSelectedPackageOption(state, offerPageState) ?? getCheapestAvailablePackageOption(offerPageState, state)
  })

  return useMemo((): [App.PricingWithValue, App.PricingWithValue] => {
    const packageBestPrice = cheapestPackageOption.memberPricing?.price || cheapestPackageOption.pricing.price
    const bedbankBestPrice = cheapestBedbankView?.totals.memberInclusive || cheapestBedbankView?.totals.inclusive || 0
    if (cheapestBedbankView && bedbankBestPrice < packageBestPrice) {
      return [{
        price: cheapestBedbankView.totals.inclusive,
        saleUnit: 'room',
        value: cheapestBedbankView.value,
        discountPercent: cheapestBedbankView.discount,
        fees: [],
      }, {
        price: cheapestBedbankView.totals.memberInclusive ?? 0,
        saleUnit: 'room',
        value: cheapestBedbankView.value,
        discountPercent: cheapestBedbankView.memberDiscount,
        fees: [],
      }]
    } else {
      // TAO does not have member pricing
      return [
        cheapestPackageOption.pricing,
        { price: 0, saleUnit: '', fees: [], value: 0, discountPercent: 0 },
      ]
    }
  },
  [cheapestPackageOption, cheapestBedbankView])
}

export default useCheapestPropertyPricing
